import React, { useEffect, useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import {
  Container,
  Input,
  DatePicker,
  Dropdown,
  Button,
  Checkbox,
  AutocompleteSearch,
  PhoneNumberInput,
} from 'components/UIElements'
import {
  MODAL_CONTENT_MAP,
  MODAL_BUTTONS_MAP,
  MODAL_CLASSES_MAP,
  DATE_FORMAT_MAP,
  INPUT_DEBOUNCE,
  PARTICIPANT_KEYS,
  FORM_FIELDS as formFields,
  PRE_SCREEN_ID_PLACEHOLDER,
} from 'utils/constants'
import { generateSampleFromFormat } from 'utils/idFormatUtils'
import STRINGS from 'utils/strings'
import SubjectIdInput from './SubjectIdInput'
import PatternInput from './PatternInput'
import { messureValidation, validateIdByFormat } from '../../../utils/participantValidation'

const InputAndDropdown = ({
  id,
  fieldDisabled,
  label,
  type,
  placeholder,
  options,
  participant,
  updateField,
  value,
}) => {
  const dropdownKey = `unit_${id}`
  const dropdownVal = participant[dropdownKey]
  const [isTouched, setIsTouched] = useState(false)
  // Memoize derived values to prevent unnecessary recalculations
  const currentOption = useMemo(() => options.find(opt => opt.key === dropdownVal), [options, dropdownVal])

  const errorText = useMemo(() => (!isTouched ? '' : messureValidation(id, dropdownVal, value)), [
    isTouched,
    id,
    dropdownVal,
    value,
  ])

  // Get placeholder only when needed
  const resolvedPlaceholder = placeholder || currentOption?.placeholder

  const handleInputChange = useCallback(
    val => {
      if (dropdownVal === 'feet/inches') {
        // Only keep digits
        let digits = val.replace(/\D/g, '')

        // Limit to 3 digits total (1 for feet + 2 for inches = 3 digits + 1 space = 4 chars)
        digits = digits.slice(0, 3)

        // Insert space after first digit if more digits exist
        if (digits.length > 1) {
          val = digits[0] + ' ' + digits.slice(1)
        } else {
          val = digits
        }
      }

      updateField(id, val)
    },
    [id, updateField, dropdownVal],
  )

  return (
    <div className='input-and-dropdown flexed start-justified'>
      <Input
        disabled={fieldDisabled}
        label={label}
        type={dropdownVal === 'feet/inches' ? 'text' : type}
        className={'ptp-form-input'}
        onChange={handleInputChange}
        onBlur={() => setIsTouched(true)}
        value={value || ''}
        id={`participant-form-${id}`}
        placeholder={resolvedPlaceholder}
        errorText={errorText}
        hasError={!!errorText}
      />
      <div className='ptp-form-dropdown'>
        <label className='label-small'>UNIT</label>
        <Dropdown
          disabled={fieldDisabled}
          className='select-dropdown'
          selected={dropdownVal}
          options={options}
          onSelect={val => {
            updateField(dropdownKey, val.key)
          }}
          id={`participant-form-dropdown-${id}`}
        />
      </div>
    </div>
  )
}

const ParticipantForm = ({
  checkedSites,
  closeModal,
  disabled,
  editable,
  hasSubjectIdValidation,
  subjectIdSiteBased = false,
  isCreate,
  leafSites,
  locales,
  openModal,
  participant,
  resetCheckedSites,
  siteSelectionList = [],
  toggleSite,
  triggerEditable,
  triggerPasswordReset,
  validateSubjectId,
  updateField,
  needToReassign,
  canWrite,
  participantDataFields,
  sitesLanguages,
  studyId,
  isSubjectIdAvailable,
  resetSubjectIdStatus,
  participantId,
  existingSubjectIds = [], // Subject IDs from existing participants for INCR auto-increment
  existingPreScreenIds = [], // Pre-screen IDs from existing participants for INCR auto-increment
  isCaregiverV1 = false,
}) => {
  useEffect(() => {
    updateField('default_locale', null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [isAutoGenerateMap, setIsAutoGenerateMap] = useState()
  const [canUpdateMap, setCanUpdateMap] = useState()
  const [extraOptions, setExtraOpts] = useState()
  const [ptpDataFieldType, setTypeMap] = useState()
  const [subjectId, setSubjectId] = useState()
  const [autoGenFormatMap, setAutoGenFormatMap] = useState()

  useEffect(() => {
    if (participantDataFields) {
      const { mandatory, optional, disabled: disabledFields } = participantDataFields
      const newMap = {}
      const typeMap = {}
      const autoGenerate = {}
      const formatMap = {}
      const fieldsArr = [...mandatory, ...optional, ...disabledFields]
      fieldsArr.forEach(field => {
        newMap[field.metadata.internal_key] = field.metadata.can_update
        typeMap[field.metadata.internal_key] = field.type
        autoGenerate[field.metadata.internal_key] = field?.metadata?.is_auto_generated ?? false
        formatMap[field.metadata.internal_key] = field.metadata.auto_gen_format
      })

      const optionsMap = fieldsArr.reduce((acc, field) => {
        const { metadata } = field
        if (!metadata?.options) return acc

        const dropdownOpts = metadata.options.map(opt => {
          const shouldTransformKey = metadata.internal_key === 'gender'
          const key = shouldTransformKey ? opt.charAt(0).toLowerCase() : opt

          return {
            key,
            text: opt,
            value: key,
          }
        })

        return {
          ...acc,
          [metadata.internal_key]: dropdownOpts,
        }
      }, {})

      setExtraOpts(optionsMap)
      setTypeMap(typeMap)
      setCanUpdateMap(newMap)
      setIsAutoGenerateMap(autoGenerate)
      setAutoGenFormatMap(formatMap)
    }
  }, [participantDataFields])

  const selectedSites = useMemo(() => Object.keys(checkedSites), [checkedSites])
  const siteIsSelected = selectedSites.length

  useEffect(() => {
    if (hasSubjectIdValidation) {
      const timeoutId = setTimeout(() => {
        if (studyId && subjectId && siteIsSelected) {
          validateSubjectId({ studyId, subjectId, participantId, selectedSites })
        }
      }, INPUT_DEBOUNCE)
      return () => clearTimeout(timeoutId)
    } else {
      resetSubjectIdStatus()
    }
  }, [
    subjectId,
    hasSubjectIdValidation,
    selectedSites,
    studyId,
    siteIsSelected,
    participantId,
    validateSubjectId,
    resetSubjectIdStatus,
  ])

  const onToggleSite = (siteID, pathName, treeLevel) => {
    resetCheckedSites()
    toggleSite(siteID, pathName, treeLevel)
    updateField('default_locale', null)
  }

  const onPasswordReset = () => {
    const { username, id } = participant
    openModal({
      content: MODAL_CONTENT_MAP.participantNewPassword,
      confirmButton: MODAL_BUTTONS_MAP.confirm,
      cancelButton: MODAL_BUTTONS_MAP.cancel,
      className: MODAL_CLASSES_MAP.confirmation,
      onConfirm: () => {
        triggerPasswordReset({ username, id })
        closeModal()
      },
    })
  }

  const list = siteSelectionList.map(row => {
    const item = { key: row[0].value, text: row[1].value, treeLevel: row[2].value, path: row[3].value }
    return item
  })

  const getFormattedHint = (key, fieldHint = '') => {
    if (participantDataFields) {
      const { optional: optionalPtpFields, mandatory } = participantDataFields
      // validate Subject ID validation_message
      if (key === 'subject_id') {
        const subjectID = [...optionalPtpFields, ...mandatory].find(({ metadata }) => metadata.internal_key === key)
        const validationMessage = subjectID.metadata?.validation_message
        if (validationMessage) {
          return validationMessage
        }
      }

      const isOptional = !!optionalPtpFields.find(field => field.metadata.internal_key === key)
      const isLocale = key === 'default_locale'
      /**
       * Default locale will never be optional even if it can be set as optional in a study config.
       * As a result, we will not display the "optional" text for the locale field.
       */
      if (isOptional && !isLocale) {
        return fieldHint ? `${fieldHint} (${STRINGS.optional})` : STRINGS.optional
      }
    }
    return fieldHint
  }

  const getFieldControlState = (isAutoGenerate, isCreate, fieldDisabled) => {
    // Case 1: Field is auto-generated during creation → allow editing before it's generated
    if (isAutoGenerate && isCreate) {
      return { isDisabled: false, showOnChange: false }
    }
    // Case 2: Otherwise, disable field if it's auto-generated or explicitly disabled
    // Always show the "on change" option when field is editable
    return {
      isDisabled: isAutoGenerate || fieldDisabled,
      showOnChange: true,
    }
  }

  const renderField = fieldProps => {
    const {
      type,
      key,
      label,
      options,
      placeholder,
      canUpdate = true,
      isAutoGenerate = false,
      hint: fieldHint,
      extraOptions,
      ptpDataFieldType,
    } = fieldProps

    const value = participant[key]
    const isSiteDisabled = disabledFields
      ? disabledFields.map(data => data.metadata.internal_key).includes('site_id')
      : false
    const notEditableAfterCreation = !isCreate && !canUpdate
    const fieldDisabled = !editable || disabled || notEditableAfterCreation

    const hint = getFormattedHint(key, fieldHint)
    const isHint = !(isAutoGenerate && isCreate)
    let newType = type
    if (key === 'date_of_birth') {
      newType = ptpDataFieldType
    }

    switch (newType) {
      case 'number':
      case 'password':
      case 'text':
        const { isDisabled, showOnChange } = getFieldControlState(isAutoGenerate, isCreate, fieldDisabled)
        return (
          <>
            {key === PARTICIPANT_KEYS.SUBJECT_ID ? (
              (() => {
                const format = autoGenFormatMap?.[key]
                const hasSiteId = format?.includes('{SITE_ID}')
                return (
                  <SubjectIdInput
                    inputKey={key}
                    type={type}
                    label={label}
                    value={value || ''}
                    updateField={updateField}
                    placeholder={
                      generateSampleFromFormat(autoGenFormatMap?.[key], studyId, selectedSites[0]) ||
                      PRE_SCREEN_ID_PLACEHOLDER
                    }
                    setSubjectId={setSubjectId}
                    fieldDisabled={subjectIdSiteBased ? isDisabled || (hasSiteId && !siteIsSelected) : isDisabled}
                    isSubjectIdAvailable={isSubjectIdAvailable}
                    resetSubjectIdStatus={resetSubjectIdStatus}
                    showOnChange={showOnChange}
                    format={autoGenFormatMap?.[key]}
                    studyId={studyId}
                    siteId={selectedSites[0]}
                    existingIds={existingSubjectIds}
                  />
                )
              })()
            ) : key === PARTICIPANT_KEYS.PRE_SCREEN_ID ? (
              <div className={`flexed end-aligned start-justified ${key}-input-wrapper`}>
                {(() => {
                  const format = autoGenFormatMap?.[key]
                  // Check if format has {STUDY_ID}, {SITE_ID}, or constants (literal text like "123", "-234")
                  const hasStudyId = format?.includes('{STUDY_ID}')
                  const hasSiteId = format?.includes('{SITE_ID}')
                  // Detect constants by removing all tokens and separators (-, spaces)
                  // Only consider it a constant if there's actual text/numbers beyond just separators
                  const textWithoutTokens = format ? format.replace(/\{[^}]+\}/g, '') : ''
                  const hasConstants = textWithoutTokens.replace(/[-\s]/g, '').length > 0
                  const shouldUsePatternInput = format && (hasStudyId || hasSiteId || hasConstants)
                  return showOnChange && shouldUsePatternInput ? (
                    <>
                      <PatternInput
                        disabled={isDisabled || (hasSiteId && !siteIsSelected)}
                        label={label}
                        pattern={format}
                        constants={{ STUDY_ID: studyId, SITE_ID: selectedSites[0] }}
                        value={value}
                        existingIds={existingPreScreenIds}
                        onIdChange={val => updateField(key, val)}
                      />
                      {value && !validateIdByFormat(value, format, { STUDY_ID: studyId, SITE_ID: selectedSites[0] }) && (
                        <div className='status-info-box'>
                          <i className='fa fa-exclamation-circle' />
                          <div className='status-info-text'>
                            <div>{STRINGS.invalidIdFormat}</div>
                            <div>
                              {STRINGS.invalidIdFormatMessage.replace(
                                '{format}',
                                generateSampleFromFormat(format, studyId, selectedSites[0]),
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    showOnChange && (
                      <>
                        <Input
                          disabled={isDisabled || (hasSiteId && !siteIsSelected)}
                          label={label}
                          type={type}
                          onChange={val => updateField(key, val)}
                          value={value || ''}
                          id={`participant-form-${key}`}
                          placeholder={
                            generateSampleFromFormat(format, studyId, selectedSites[0]) || PRE_SCREEN_ID_PLACEHOLDER
                          }
                        />
                        {value &&
                          format &&
                          !validateIdByFormat(value, format, { STUDY_ID: studyId, SITE_ID: selectedSites[0] }) && (
                            <div className='status-info-box'>
                              <i className='fa fa-exclamation-circle' />
                              <div className='status-info-text'>
                                <div>{STRINGS.invalidIdFormat}</div>
                                <div>
                                  {STRINGS.invalidIdFormatMessage.replace(
                                    '{format}',
                                    generateSampleFromFormat(format, studyId, selectedSites[0]),
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                      </>
                    )
                  )
                })()}
              </div>
            ) : key === PARTICIPANT_KEYS.DATE_OF_BIRTH ? (
              <Input
                disabled={fieldDisabled}
                label={'BIRTH YEAR'}
                type={newType}
                onChange={val => updateField(key, val)}
                value={value || ''}
                id={`participant-form-${key}`}
                placeholder={'2000'}
              />
            ) : [PARTICIPANT_KEYS.HEIGHT, PARTICIPANT_KEYS.WEIGHT].includes(key) ? (
              <InputAndDropdown
                id={key}
                fieldDisabled={fieldDisabled}
                label={label}
                type={type}
                placeholder={placeholder}
                options={options}
                updateField={updateField}
                value={value || ''}
                participant={participant}
              />
            ) : (
              <Input
                disabled={fieldDisabled}
                label={label}
                type={type}
                onChange={val => updateField(key, val)}
                value={type === 'password' && !isCreate ? 'XXXXXX' : value || ''}
                id={`participant-form-${key}`}
                placeholder={placeholder}
              />
            )}

            {isHint && hint && [PARTICIPANT_KEYS.SUBJECT_ID, PARTICIPANT_KEYS.PRE_SCREEN_ID].includes(key) && (
              <span className='hint'>{hint}</span>
            )}
          </>
        )
      case 'check':
        return (
          key === PARTICIPANT_KEYS.IS_CAREGIVER &&
          isCaregiverV1 && (
            <div className='flexed column start-justified start-aligned'>
              <Checkbox
                toggle
                label={label}
                disabled={fieldDisabled}
                checked={value}
                id={`participant-form-${key}`}
                onClick={() => updateField(key, !value)}
              />
              {hint && <span className='hint'>{hint}</span>}
            </div>
          )
        )
      case 'date': {
        const isStartDate = key === PARTICIPANT_KEYS.START_DATE
        return (
          <>
            <label className='label-small'>{label}</label>
            <DatePicker
              disabledDays={isStartDate ? undefined : { after: new Date() }}
              disabled={fieldDisabled}
              initialDate={value ? moment(value, DATE_FORMAT_MAP.datePicker) : value}
              onDayChange={(date, options) =>
                updateField(key, date ? date.format(DATE_FORMAT_MAP.datePicker) : options.state.typedValue)
              }
              id={`participant-form-${key}`}
              placeholder={placeholder}
            />
            {hint && <span className='hint'>{hint}</span>}
          </>
        )
      }
      case 'select': {
        const isLocale = key === PARTICIPANT_KEYS.DEFAULT_LOCALE

        if (isLocale) {
          const selectedSitesLanguages = selectedSites
            .filter(siteId => sitesLanguages[siteId])
            .map(siteId => sitesLanguages[siteId]?.enforced_language?.languages)
            .filter(item => item)
            .flat()

          // Options will default to locales options if no options are provided from module/CreateParticipant.js
          const dropdownOptions = selectedSitesLanguages.length ? selectedSitesLanguages : locales
          return (
            <div className='site-locale-wrapper'>
              {!isSiteDisabled && (
                <>
                  <p className='label-small sites flexed center-aligned start-justified'>
                    {STRINGS.site}
                    {needToReassign && (
                      <span className='flexed'>
                        <i className='fas fa-exclamation-circle' />
                        {STRINGS.reassignToLeafSite}
                      </span>
                    )}
                  </p>
                  <AutocompleteSearch
                    checked={checkedSites}
                    disabled={!editable || disabled || notEditableAfterCreation}
                    filter={leafSites}
                    initialValue={checkedSiteName}
                    list={list}
                    id='participant-site'
                    noInitialValue={isCreate}
                    placeholder={STRINGS.selectSite}
                    toggleItem={onToggleSite}
                  />
                </>
              )}
              <label className='label-small'>{label}</label>
              <Dropdown
                disabled={fieldDisabled || !siteIsSelected}
                className='select-dropdown'
                selected={siteIsSelected ? value : undefined}
                options={dropdownOptions}
                onSelect={val => updateField(key, val.id)}
                id={`participant-form-${key}`}
                placeholder={siteIsSelected ? STRINGS.selectLocale : STRINGS.selectSiteFirst}
              />
              {hint && <span className='hint'>{hint}</span>}
            </div>
          )
        }
        const newOpts = extraOptions && extraOptions?.length ? extraOptions : options
        return (
          <>
            <label className='label-small'>{label}</label>
            <Dropdown
              disabled={fieldDisabled}
              className='select-dropdown'
              selected={value}
              options={newOpts}
              onSelect={val => updateField(key, val.value)}
              id={`participant-form-${key}`}
            />
            {hint && <span className='hint'>{hint}</span>}
          </>
        )
      }
      case 'phone':
        return (
          <div className='phone'>
            <p className='label-small'>{label}</p>
            <PhoneNumberInput
              disabled={fieldDisabled}
              onChange={val => updateField(key, val)}
              onCountryChange={() => updateField(key, '')}
              value={value || ''}
              id='participant-phone-number'
            />
            {hint && <span className='hint'>{hint}</span>}
            <p className='disclaimer'>
              By entering this mobile phone number, I verify that the participant has agreed to use this mobile phone
              number for the purpose of the study and that the participant has given me permission to enter this mobile
              phone number.
            </p>
          </div>
        )
      default:
        return null
    }
  }

  const checkedSiteInfo = siteSelectionList.filter(site => site[0].value == Object.keys(checkedSites)[0])[0]
  const checkedSiteName = checkedSiteInfo && checkedSiteInfo.length ? checkedSiteInfo[1].value : ''
  const hasLoggedIn = !!participant.first_login_timestamp
  const { mandatory = [], optional = [], disabled: disabledFields = [] } = participantDataFields || {}
  const allowedKeys = new Set([...mandatory, ...optional].map(field => field.metadata.internal_key))
  const disabledKeys = new Set(disabledFields.map(field => field.metadata.internal_key))
  const filteredFormFields = formFields.filter(field => allowedKeys.has(field.key) && !disabledKeys.has(field.key))
  return (
    <Container className='participant-form' centered flex column>
      <div className='title'>Participant Details</div>
      <span className='hint'>All fields are required unless marked as optional.</span>
      {participant.username && <h5>{participant.username}</h5>}
      {filteredFormFields.map(field => {
        const { type, key, text, options, placeholder, hint } = field
        return (
          <div key={key}>
            {renderField({
              type,
              key,
              label: text,
              options,
              placeholder,
              canUpdate: canUpdateMap?.[field.key],
              isAutoGenerate: isAutoGenerateMap?.[field.key],
              hint,
              extraOptions: extraOptions?.[field.key],
              ptpDataFieldType: ptpDataFieldType?.[field.key],
            })}
          </div>
        )
      })}
      {!isCreate && editable && canWrite && hasLoggedIn && (
        <div className='reset-password flexed'>
          <span>{`Last password reset: ${moment(participant.last_updated).format(DATE_FORMAT_MAP.main)}`}</span>
          <Button link onClick={onPasswordReset}>
            Reset Password
          </Button>
        </div>
      )}
      {!editable && !isCreate && disabled && (
        <Button link content='Edit' icon='fas fa-pencil-alt' className='edit-form-button' onClick={triggerEditable} />
      )}
    </Container>
  )
}

ParticipantForm.propTypes = {
  checkedSites: PropTypes.object,
  closeModal: PropTypes.func,
  disabled: PropTypes.bool,
  editable: PropTypes.bool,
  isCreate: PropTypes.bool,
  canWrite: PropTypes.bool,
  hasSubjectIdValidation: PropTypes.bool,
  subjectIdSiteBased: PropTypes.bool,
  leafSites: PropTypes.arrayOf(PropTypes.number),
  locales: PropTypes.array,
  openModal: PropTypes.func,
  participant: PropTypes.object,
  resetCheckedSites: PropTypes.func,
  siteSelectionList: PropTypes.arrayOf(PropTypes.array),
  toggleSite: PropTypes.func,
  triggerEditable: PropTypes.func,
  triggerPasswordReset: PropTypes.func,
  updateField: PropTypes.func,
  needToReassign: PropTypes.bool,
  participantDataFields: PropTypes.object,
  existingSubjectIds: PropTypes.array,
  existingPreScreenIds: PropTypes.array,
}

export default ParticipantForm
